<template>
  <div class="cluster-profiles-page">
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <h1 class="mb-4">{{ content.title }}</h1>
          <p class="lead mb-5">{{ content.description }}</p>
          
          <!-- Loading State -->
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p>{{ content.loading }}</p>
          </div>
          
                     <!-- Cluster Profiles Grid -->
           <div v-else class="cluster-grid">
             <div 
               v-for="(cluster, index) in clusterProfiles" 
               :key="index"
               class="cluster-card"
             >
               <div class="cluster-header" @click="toggleCluster(index)">
                 <div class="header-content">
                   <h3 class="cluster-title">{{ cluster.macro_profile }}</h3>
                   <div class="apt-badges">
                     <router-link 
                       v-for="apt in cluster.APT" 
                       :key="apt"
                       :to="`/apt-map?apt=${apt}`"
                       class="apt-badge-link"
                     >
                       <span class="apt-badge">{{ apt }}</span>
                     </router-link>
                   </div>
                 </div>
                 <div class="collapse-icon" :class="{ 'expanded': expandedClusters[index] }">
                   <i class="icon">▼</i>
                 </div>
               </div>
               
               <div class="cluster-content" v-show="expandedClusters[index]">
                <!-- Cluster Criteria -->
                <div v-if="cluster.cluster_criteria" class="criteria-section">
                  <h4 class="section-title">{{ content.clusterCriteria }}</h4>
                  <div class="criteria-info">
                    <div class="criteria-badges">
                      <span class="criteria-badge">
                        <strong>{{ content.visibility }}:</strong> {{ cluster.cluster_criteria.Visibility }}
                      </span>
                      <span class="criteria-badge">
                        <strong>{{ content.destructiveness }}:</strong> {{ cluster.cluster_criteria.Destructiveness }}
                      </span>
                    </div>
                    <p v-if="cluster.cluster_criteria.description" class="criteria-description">
                      {{ cluster.cluster_criteria.description }}
                    </p>
                  </div>
                </div>
                
                <!-- Central Traits -->
                <div class="traits-section">
                  <h4 class="section-title">{{ content.centralTraits }}</h4>
                  <div class="traits-grid">
                    <div 
                      v-for="(trait, traitName) in cluster.central_traits" 
                      :key="traitName"
                      class="trait-item"
                    >
                      <strong class="trait-name">{{ traitName }}:</strong>
                      <span class="trait-description">{{ trait }}</span>
                    </div>
                  </div>
                </div>
                
                <!-- Operational Profile -->
                <div class="operational-section">
                  <h4 class="section-title">{{ content.operationalProfile }}</h4>
                  <p class="operational-description">{{ cluster.operational_profile }}</p>
                </div>
                
                <!-- Defense Strategies -->
                <div class="defense-section">
                  <h4 class="section-title">{{ content.defenseStrategies }}</h4>
                  
                  <!-- Key Traits and Defensive Focus -->
                  <div v-if="cluster.Defense.key_traits || cluster.Defense.defensive_focus" class="defense-overview">
                    <div v-if="cluster.Defense.key_traits" class="overview-item">
                      <strong>{{ content.keyTraits }}:</strong>
                      <span>{{ cluster.Defense.key_traits }}</span>
                    </div>
                    <div v-if="cluster.Defense.defensive_focus" class="overview-item">
                      <strong>{{ content.defensiveFocus }}:</strong>
                      <span>{{ cluster.Defense.defensive_focus }}</span>
                    </div>
                  </div>
                  
                  <!-- Main Mitigation -->
                  <div class="defense-category">
                    <h5 class="defense-category-title">{{ content.mainMitigation }}</h5>
                    <ul class="defense-list">
                      <li 
                        v-for="(mitigation, idx) in cluster.Defense.main_mitigation" 
                        :key="idx"
                        class="defense-item"
                      >
                        {{ mitigation }}
                      </li>
                    </ul>
                  </div>
                  
                  <!-- Preventive Defense -->
                  <div class="defense-category">
                    <h5 class="defense-category-title">{{ content.preventiveDefense }}</h5>
                    <ul class="defense-list">
                      <li 
                        v-for="(defense, idx) in cluster.Defense.preventive_defense" 
                        :key="idx"
                        class="defense-item"
                      >
                        {{ defense }}
                      </li>
                    </ul>
                  </div>
                  
                  <!-- Disruptive Measure -->
                  <div class="defense-category">
                    <h5 class="defense-category-title">{{ content.disruptiveMeasure }}</h5>
                    <p class="disruptive-description">{{ cluster.Defense.disruptive_measure }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataService from '../services/DataServices'
import { languageStore } from '../stores/languageStore'

export default {
  name: 'ClusterProfiles',
  data() {
    return {
      clusterProfiles: [],
      loading: true,
      expandedClusters: {},
      content: {
        en: {
          title: 'Cluster Profiles',
          description: 'Behavioral analysis of APT groups organized by psychological and operational characteristics. Each cluster represents a distinct threat profile with specific defense strategies.',
          loading: 'Loading cluster profiles...',
          clusterCriteria: 'Cluster Criteria',
          visibility: 'Visibility',
          destructiveness: 'Destructiveness',
          centralTraits: 'Central Traits',
          operationalProfile: 'Operational Profile',
          defenseStrategies: 'Defense Strategies',
          keyTraits: 'Key Traits',
          defensiveFocus: 'Defensive Focus',
          mainMitigation: 'Main Mitigation',
          preventiveDefense: 'Preventive Defense',
          disruptiveMeasure: 'Disruptive Measure'
        },
        es: {
          title: 'Perfiles de Clusters',
          description: 'Análisis conductual de grupos APT organizados por características psicológicas y operacionales. Cada cluster representa un perfil de amenaza distinto con estrategias de defensa específicas.',
          loading: 'Cargando perfiles de clusters...',
          clusterCriteria: 'Criterios del Cluster',
          visibility: 'Visibilidad',
          destructiveness: 'Destructividad',
          centralTraits: 'Rasgos Centrales',
          operationalProfile: 'Perfil Operacional',
          defenseStrategies: 'Estrategias de Defensa',
          keyTraits: 'Rasgos Clave',
          defensiveFocus: 'Enfoque Defensivo',
          mainMitigation: 'Mitigación Principal',
          preventiveDefense: 'Defensa Preventiva',
          disruptiveMeasure: 'Medida Disruptiva'
        }
      }
    }
  },
  computed: {
    language() {
      return languageStore.getLanguage()
    },
    content() {
      return this.content[this.language]
    }
  },
  watch: {
    language() {
      this.loadClusterProfiles()
    }
  },
  methods: {
    loadClusterProfiles() {
      this.loading = true
      const currentLang = this.language
      const clusterProfileUrl = `https://raw.githubusercontent.com/Base4Security/PRISM/refs/heads/main/aptGroups/ClusterProfile${currentLang.toUpperCase()}.json`
      
      DataService.fetchJSON(clusterProfileUrl)
        .then(data => {
          if (data && data.ClusterProfile) {
            this.clusterProfiles = data.ClusterProfile
          } else {
            this.clusterProfiles = []
          }
          this.loading = false
        })
        .catch(error => {
          console.error('Error loading cluster profiles:', error)
          this.loading = false
        })
    },
    toggleCluster(index) {
      this.expandedClusters[index] = !this.expandedClusters[index]
    }
  },
  mounted() {
    this.loadClusterProfiles()
  }
}
</script>

<style scoped>
.cluster-profiles-page {
  padding: 20px;
  background: var(--mm-custom-color-1);
}

.lead {
  color: var(--mm-custom-color-5);
  font-size: 1.1rem;
  opacity: 0.9;
}

/* Loading styles */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: var(--mm-custom-color-3);
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--mm-custom-color-2);
  min-height: 400px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(220, 120, 15, 0.3);
  border-top: 4px solid #DC780F;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-container p {
  color: var(--mm-custom-color-5);
  margin: 0;
  font-size: 1.1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Cluster Grid */
.cluster-grid {
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: 30px;
}

/* Cluster Card */
.cluster-card {
  background: var(--mm-custom-color-3);
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  border: 2px solid var(--mm-custom-color-2);
  transition: all 0.3s ease;
}

.cluster-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(220, 120, 15, 0.3);
}

/* Cluster Header */
.cluster-header {
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--mm-custom-color-2);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  transition: all 0.3s ease;
}

.cluster-header:hover {
  background: rgba(220, 120, 15, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin: -15px -15px 10px -15px;
}

.header-content {
  flex: 1;
}

.collapse-icon {
  margin-left: 15px;
  transition: transform 0.3s ease;
  color: var(--mm-custom-color-2);
  font-size: 1.2rem;
}

.collapse-icon.expanded {
  transform: rotate(180deg);
}

.collapse-icon .icon {
  display: block;
  transition: transform 0.3s ease;
}

.cluster-title {
  color: var(--mm-custom-color-2);
  font-size: 1.4rem;
  margin-bottom: 15px;
  font-weight: bold;
}

.apt-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.apt-badge-link {
  text-decoration: none;
  transition: all 0.3s ease;
}

.apt-badge-link:hover {
  transform: translateY(-2px);
  text-decoration: none;
}

.apt-badge {
  background: var(--mm-custom-color-2);
  color: var(--mm-custom-color-5);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  display: inline-block;
}

.apt-badge-link:hover .apt-badge {
  background: var(--custom-color-5);
  box-shadow: 0 4px 8px rgba(220, 120, 15, 0.3);
}

/* Section Titles */
.section-title {
  color: var(--mm-custom-color-2);
  font-size: 1.2rem;
  margin-bottom: 15px;
  font-weight: bold;
  border-bottom: 1px solid rgba(220, 120, 15, 0.3);
  padding-bottom: 8px;
}

/* Traits Section */
.traits-section {
  margin-bottom: 25px;
}

.traits-grid {
  display: grid;
  gap: 12px;
}

.trait-item {
  background: var(--mm-custom-color-1);
  padding: 12px 15px;
  border-radius: 8px;
  border-left: 4px solid var(--mm-custom-color-2);
}

.trait-name {
  color: var(--mm-custom-color-2);
  display: block;
  margin-bottom: 5px;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.trait-description {
  color: var(--mm-custom-color-5);
  font-size: 0.95rem;
  line-height: 1.4;
}

/* Criteria Section */
.criteria-section {
  margin-bottom: 25px;
}

.criteria-info {
  background: var(--mm-custom-color-1);
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid var(--mm-custom-color-2);
}

.criteria-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.criteria-badge {
  background: #0d5e6f;
  color: var(--mm-custom-color-5);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: bold;
}

.criteria-description {
  color: var(--mm-custom-color-5);
  font-size: 0.9rem;
  margin: 10px 0 0 0;
  font-style: italic;
}

/* Defense Overview */
.defense-overview {
  background: var(--mm-custom-color-1);
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid var(--mm-custom-color-2);
  margin-bottom: 20px;
}

.overview-item {
  margin-bottom: 10px;
  color: var(--mm-custom-color-5);
  font-size: 0.95rem;
  line-height: 1.5;
}

.overview-item:last-child {
  margin-bottom: 0;
}

.overview-item strong {
  color: var(--mm-custom-color-2);
  margin-right: 8px;
}

/* Operational Section */
.operational-section {
  margin-bottom: 25px;
}

.operational-description {
  color: var(--mm-custom-color-5);
  line-height: 1.6;
  font-size: 0.95rem;
  background: var(--mm-custom-color-1);
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid var(--mm-custom-color-2);
}

/* Defense Section */
.defense-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.defense-category {
  background: var(--mm-custom-color-1);
  padding: 20px;
  border-radius: 10px;
  border: 1px solid rgba(220, 120, 15, 0.2);
}

.defense-category-title {
  color: var(--mm-custom-color-2);
  font-size: 1.1rem;
  margin-bottom: 15px;
  font-weight: bold;
}

.defense-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.defense-item {
  color: var(--mm-custom-color-5);
  padding: 8px 0;
  border-bottom: 1px solid rgba(220, 120, 15, 0.1);
  line-height: 1.5;
  font-size: 0.9rem;
}

.defense-item:last-child {
  border-bottom: none;
}

.defense-item::before {
  content: "•";
  color: var(--mm-custom-color-2);
  font-weight: bold;
  margin-right: 8px;
}

.disruptive-description {
  color: var(--mm-custom-color-5);
  line-height: 1.5;
  font-size: 0.9rem;
  margin: 0;
  font-style: italic;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .cluster-grid {
    gap: 20px;
  }
  
  .cluster-card {
    padding: 20px;
  }
  
  .cluster-title {
    font-size: 1.2rem;
  }
  
  .apt-badges {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .cluster-profiles-page {
    padding: 15px;
  }
  
  .cluster-card {
    padding: 15px;
  }
  
  .defense-category {
    padding: 15px;
  }
}
</style>
